/**
 * Liquiditätsplanung transaction categorisation axis (#1612).
 *
 * The stored category is the L2 ``ledger_category`` from the 2-level GuV
 * vocabulary (``config/ontology/ledger_category.yaml``) — seeded from the
 * Jahresabschluss GuV (LBD/ADNOVA, standardized ADNOVA/SKR14), so it is
 * DATEV-reconcilable and cross-farm benchmark-able. This is a SEPARATE
 * dimension from the 9 document-classifier domains (which stay the axis for
 * *documents*). The 9 domains survive here only as a learned-crosswalk
 * SUGGESTION prior (``crosswalkDomainToCategory``), never the stored category.
 *
 * Keep this in lock-step with the YAML — ``ledgerCategory.test.ts`` drift-guards
 * the ids/groups against the config, and the backend enforces the same enum
 * (``backend/src/api/v1/endpoints/twin/liquiditaet.py``, drift-guarded against
 * the same config in its tests).
 */

/** ``in`` = Einnahme · ``out`` = Ausgabe · ``both`` = straddles the sign. */
export type LedgerDirection = 'in' | 'out' | 'both';

/** ``both`` ⚖️ (cash + accrual) · ``cash_only`` 💶 · ``accrual_only`` 📊.
 *  The liquidity picker reads ``both`` + ``cash_only``. */
export type LedgerView = 'both' | 'cash_only' | 'accrual_only';

/** The 6 functional L1 groups + a crosscutting bucket. */
export type LedgerGroup =
  | 'income_operating'
  | 'subsidy'
  | 'expense_operating'
  | 'investment'
  | 'financing'
  | 'private'
  | 'crosscutting';

export interface LedgerGroupMeta {
  id: LedgerGroup;
  label: string;
  direction: LedgerDirection;
}

/** L1 groups in config order (drives the picker's optgroup order). */
export const L1_GROUPS: readonly LedgerGroupMeta[] = [
  { id: 'income_operating', label: 'Betriebseinnahmen', direction: 'in' },
  { id: 'subsidy', label: 'Förderungen & Zuschüsse', direction: 'in' },
  { id: 'expense_operating', label: 'Betriebsausgaben', direction: 'out' },
  { id: 'investment', label: 'Investitionen & Instandhaltung', direction: 'out' },
  { id: 'financing', label: 'Finanzierung', direction: 'both' },
  { id: 'private', label: 'Privat', direction: 'both' },
  { id: 'crosscutting', label: 'Übergreifend', direction: 'both' },
] as const;

export interface CategoryMeta {
  group: LedgerGroup;
  label: string;
  direction: LedgerDirection;
  view: LedgerView;
}

/**
 * The L2 categories, keyed by id → meta, in config order. ``LEDGER_CATEGORIES``
 * (below) is derived from the key order so the two never drift.
 *
 * ``unsorted`` is the explicit not-yet-categorised bucket (mirrors the document
 * axis' ``unsorted`` domain): both-direction + both-view so it is always a valid
 * stored value, but the economic picker de-emphasizes/excludes it.
 */
const CATEGORY_DEFS = {
  // L1 group 1 — income_operating — Betriebseinnahmen (in)
  rev_crops: { group: 'income_operating', label: 'Umsatzerlöse Pflanzenproduktion', direction: 'in', view: 'both' },
  rev_livestock: { group: 'income_operating', label: 'Tierproduktion', direction: 'in', view: 'both' },
  rev_forestry: { group: 'income_operating', label: 'Forst & Jagd', direction: 'in', view: 'both' },
  rev_services: { group: 'income_operating', label: 'Dienstleistungen/Nebenbetriebe', direction: 'in', view: 'both' },
  rev_rent: { group: 'income_operating', label: 'Pacht- & Mieterträge', direction: 'in', view: 'both' },
  rev_asset_disposal: { group: 'income_operating', label: 'Erlöse aus Anlagenabgang', direction: 'in', view: 'both' },
  rev_other: { group: 'income_operating', label: 'Sonstige betriebl. Erträge', direction: 'in', view: 'both' },

  // L1 group 2 — subsidy — Förderungen & Zuschüsse (in)
  sub_basic: { group: 'subsidy', label: 'Einkommensgrundstützung', direction: 'in', view: 'both' },
  sub_young: { group: 'subsidy', label: 'Junglandwirteförderung', direction: 'in', view: 'both' },
  sub_agz: { group: 'subsidy', label: 'Ausgleichszulage', direction: 'in', view: 'both' },
  sub_oekoregelung: { group: 'subsidy', label: 'Öko-Regelungen (ÖR 1–7)', direction: 'in', view: 'both' },
  sub_aukm: { group: 'subsidy', label: 'AUKM (KULAP/FAKT)', direction: 'in', view: 'both' },
  sub_conversion: { group: 'subsidy', label: 'Umstellungs-/Bio-Beibehaltungsprämie', direction: 'in', view: 'both' },
  sub_agrardiesel: { group: 'subsidy', label: 'Agrardiesel-Rückvergütung', direction: 'in', view: 'both' },
  sub_other: { group: 'subsidy', label: 'Sonstige Zuschüsse', direction: 'in', view: 'both' },

  // L1 group 3 — expense_operating — Betriebsausgaben (out)
  exp_seed: { group: 'expense_operating', label: 'Saat-/Pflanzgut', direction: 'out', view: 'both' },
  exp_fertilizer: { group: 'expense_operating', label: 'Düngemittel', direction: 'out', view: 'both' },
  exp_cropprotection: { group: 'expense_operating', label: 'Pflanzenschutz', direction: 'out', view: 'both' },
  exp_feed: { group: 'expense_operating', label: 'Futtermittel', direction: 'out', view: 'both' },
  exp_fuel: { group: 'expense_operating', label: 'Treib-/Schmierstoffe', direction: 'out', view: 'both' },
  exp_energy: { group: 'expense_operating', label: 'Energie & Wasser', direction: 'out', view: 'both' },
  exp_contracting: { group: 'expense_operating', label: 'Lohnarbeit & Maschinenmiete', direction: 'out', view: 'both' },
  exp_material_other: { group: 'expense_operating', label: 'Sonstiges Material', direction: 'out', view: 'both' },
  exp_wages: { group: 'expense_operating', label: 'Löhne & Gehälter', direction: 'out', view: 'both' },
  exp_socialsec: { group: 'expense_operating', label: 'Soziale Abgaben & SVLFG', direction: 'out', view: 'both' },
  exp_lease: { group: 'expense_operating', label: 'Pachtaufwand ldw. Flächen', direction: 'out', view: 'both' },
  exp_insurance: { group: 'expense_operating', label: 'Betriebsversicherungen', direction: 'out', view: 'both' },
  exp_contributions: { group: 'expense_operating', label: 'Beiträge & Abgaben', direction: 'out', view: 'both' },
  exp_maintenance: { group: 'expense_operating', label: 'Unterhaltung/Reparaturen', direction: 'out', view: 'both' },
  exp_admin_it: { group: 'expense_operating', label: 'Verwaltung & IT/Software', direction: 'out', view: 'both' },
  exp_legal_advisory: {
    group: 'expense_operating',
    label: 'Rechts-/Notar-/Beratungskosten',
    direction: 'out',
    view: 'both',
  },
  exp_taxes: { group: 'expense_operating', label: 'Betriebliche Steuern', direction: 'out', view: 'both' },
  exp_other: { group: 'expense_operating', label: 'Sonstiger Betriebsaufwand', direction: 'out', view: 'both' },

  // L1 group 4 — investment — Investitionen & Instandhaltung (out)
  inv_machinery: { group: 'investment', label: 'Maschinen & Technik (Kauf)', direction: 'out', view: 'cash_only' },
  inv_buildings: { group: 'investment', label: 'Gebäude & Bauten', direction: 'out', view: 'cash_only' },
  inv_improvement: { group: 'investment', label: 'Größere Instandhaltung/Ausbau', direction: 'out', view: 'cash_only' },

  // L1 group 5 — financing — Finanzierung (both)
  fin_loan_in: { group: 'financing', label: 'Darlehensaufnahme', direction: 'in', view: 'cash_only' },
  fin_principal: { group: 'financing', label: 'Tilgung Darlehen/Leasing', direction: 'out', view: 'cash_only' },
  fin_interest: { group: 'financing', label: 'Zinsen', direction: 'out', view: 'both' },
  fin_fees: { group: 'financing', label: 'Bank-/Dispo-/Abschlussgebühren', direction: 'out', view: 'both' },

  // L1 group 6 — private — Privat (both)
  priv_draw: { group: 'private', label: 'Privatentnahmen', direction: 'out', view: 'cash_only' },
  priv_deposit: { group: 'private', label: 'Privateinlagen', direction: 'in', view: 'cash_only' },
  priv_share: {
    group: 'private',
    label: 'Privatanteile (private Nutzung Betriebsvermögen)',
    direction: 'both',
    view: 'accrual_only',
  },

  // Cross-cutting
  vat: { group: 'crosscutting', label: 'Umsatzsteuer/Vorsteuer', direction: 'both', view: 'cash_only' },
  noncash_depreciation: { group: 'crosscutting', label: 'Abschreibungen', direction: 'both', view: 'accrual_only' },
  noncash_inventory: { group: 'crosscutting', label: 'Bestandsveränderungen', direction: 'both', view: 'accrual_only' },
  // #2679: movement between the tenant's OWN accounts — neither income nor expense.
  // ``cash_only`` because it really moves a bank balance with no accrual meaning.
  transfer_internal: {
    group: 'crosscutting',
    label: 'Umbuchung zwischen eigenen Konten',
    direction: 'both',
    view: 'cash_only',
  },
  unsorted: { group: 'crosscutting', label: 'Unsortiert', direction: 'both', view: 'both' },
  // ``satisfies`` validates each entry against CategoryMeta WITHOUT widening the
  // key type — so ``keyof typeof CATEGORY_DEFS`` below is the literal id union,
  // making this object the single source of truth (no hand-written union to drift,
  // no ``as`` casts to hide a mismatch). The drift-guard test pins these ids
  // against the YAML config.
} satisfies Record<string, CategoryMeta>;

/** The L2 category id — the stored value. Derived from ``CATEGORY_DEFS`` keys. */
export type LedgerCategory = keyof typeof CATEGORY_DEFS;

/** The L2 category ids in config order (the stored-value enum source). */
export const LEDGER_CATEGORIES = Object.keys(CATEGORY_DEFS) as readonly LedgerCategory[];

/** id → meta, typed. */
export const CATEGORY_META: Record<LedgerCategory, CategoryMeta> = CATEGORY_DEFS;

/** The explicit not-yet-categorised stored value (mirrors the document axis). */
export const UNSORTED: LedgerCategory = 'unsorted';

export function isLedgerCategory(value: unknown): value is LedgerCategory {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(CATEGORY_DEFS, value);
}

/** Resolve a (possibly malformed) id to its German GuV name; unknown ⇒ Unsortiert. */
export function categoryLabel(value: unknown): string {
  return isLedgerCategory(value) ? CATEGORY_META[value].label : CATEGORY_META.unsorted.label;
}

/**
 * Crosswalk prior (9-domain → candidate L2 categories). Hand-authored; keys are
 * ``config/taxonomy-v2/INDEX.yaml`` domain ids. A runtime SUGGESTION only — never
 * the stored category. Kept in lock-step with the YAML ``crosswalk`` block.
 */
const CROSSWALK: Record<string, LedgerCategory[]> = {
  fields: ['exp_seed', 'exp_fertilizer', 'exp_cropprotection'],
  machinery: ['exp_maintenance', 'inv_machinery', 'exp_fuel'],
  finance: ['fin_interest', 'fin_fees', 'fin_principal', 'sub_basic', 'sub_oekoregelung'],
  employees: ['exp_wages', 'exp_socialsec'],
  buildings: ['inv_buildings', 'inv_improvement', 'exp_maintenance'],
  administration: ['exp_admin_it', 'exp_legal_advisory', 'exp_contributions', 'exp_taxes'],
  personal: ['priv_draw', 'priv_deposit'],
  livestock: ['rev_livestock', 'exp_feed'],
  unsorted: [],
};

/**
 * Map a legacy 9-domain id to a suggested ledger_category (the shortlist head),
 * falling back to ``unsorted`` when there is no prior or the domain is unknown.
 * Used by the mapper's migration fallback (legacy ``domain``-only rows) and as
 * the form's default-seeder. ``.shortlist(domain)`` returns the full candidate
 * list so the form can float it to the top of the picker.
 */
export const crosswalkDomainToCategory = Object.assign(
  (domain: unknown): LedgerCategory => {
    const list = typeof domain === 'string' ? CROSSWALK[domain] : undefined;
    return list && list.length > 0 ? list[0] : UNSORTED;
  },
  {
    /** The crosswalk candidate shortlist for a domain (empty when none/unknown). */
    shortlist(domain: unknown): LedgerCategory[] {
      const list = typeof domain === 'string' ? CROSSWALK[domain] : undefined;
      return list ? [...list] : [];
    },
  },
);
